import React, { useEffect, useState } from 'react';
import './table.css';
import {
  Box,
  Button,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  TableCell,
  TextField,
  Tooltip,
  useTheme
} from '@mui/material';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { useUpdateModalStore } from '../../zustandStore/store.update';
import { Delete, SearchRounded } from '@mui/icons-material';
import { ReactJSXElement } from '@emotion/react/types/jsx-namespace';
import { DataGrid, GridColDef, GridFilterModel, GridRowsProp } from '@mui/x-data-grid';


import { useAuth } from 'src/hooks/useAuth';
import { gridClasses } from '@mui/system';

interface header {
  displayName: string;
  key: string;
  cssStyle?: React.CSSProperties;
  minWidth?: number;
  maxWidth?: number;
  width?: number;

}

type rows = {
  key: string;
  compute: (data: any) => JSX.Element;
};

export interface DynamicTableProps {
  headers?: GridColDef[];
  rows?: any[];
  searchElement?: ReactJSXElement;
  sort?: 'asc' | 'desc';
  onSort?: (header: string) => void;
  sortByField?: string;
  page?: number;
  totalPage?: number;
  rowsPerPage?: number;
  handleChangePage?: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  handleChangeRowsPerPage?: (
    event: number
  ) => void;
  showPagination?: boolean;
}

export const DynamicTable = (props: DynamicTableProps) => {
  const {
    headers,
    rows,
    searchElement,
    sort = 'desc',
    onSort,
    sortByField,
    totalPage,
    handleChangeRowsPerPage,
    handleChangePage,
    page,
    rowsPerPage,
    showPagination = false
  } = props;


  return (
    <>
      <div style={{ height: '100%', width: '95%', margin: '3% auto', display: 'flex', flexDirection: 'column' }}>

          <DataGrid
            rowHeight={50}
            pagination={true}
            pageSizeOptions={[50]}
            filterMode={handleChangeRowsPerPage ? 'server' : 'client'}
            paginationMode={handleChangeRowsPerPage ? 'server' : 'client'}
            onPaginationModelChange={(e) => {
              if(handleChangePage) {
                handleChangePage(null, e.page);
              }
              if(handleChangeRowsPerPage) {
                handleChangeRowsPerPage(e.pageSize);
              }
            }}
            initialState={{
              pagination: {
                paginationModel: {
                  page: page ? page : 0,
                  pageSize: rowsPerPage ? rowsPerPage : 50
                }
              }
            }}
            getRowHeight={(params) => {
              if(params.model.hasOwnProperty('rowHeight')) {
                return params.model.rowHeight;
              }else{
                return 'auto';
              }
            }}


            rowCount={totalPage ? totalPage : rows.length}
            sx={{
              borderRadius: '5px',
              height: '100%',
              boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',

            }}

            rows={rows} columns={headers.map(header => ({ ...header, flex: 1 }))} />


      </div>
    </>);



};

type SourceAction = {
  EDIT?: string;
  UPDATE?: string;
  CREATE?: string;
};

export interface ActionCellProps<T> {
  editEndpoint?: string;
  deleteEndpoint?: string;
  payload?: T;
  callback?: () => void;
  updateFields?: any;
  sourceAction?: SourceAction;
  children?: React.ReactNode;
}

export const ActionCell = (props: ActionCellProps<any>) => {
  const actions = useUpdateModalStore((state) => state.actions);
  const theme = useTheme();
  return (
    <TableCell align="right">


      {/* Only render the Edit button if editEndpoint is provided */}
      {props.editEndpoint && (
        <Tooltip title="Edit" arrow>
          <IconButton
            onClick={(e) => {

              actions.setModal({
                modalOpen: true,
                payload: props.payload,
                callback: props.callback,
                editEndpoint: props.editEndpoint,
                isEdit: true,
                isDelete: false,
                updateFields: props.updateFields,
                sourceAction: props.sourceAction?.EDIT
              });
            }
            }
            sx={{
              '&:hover': {
                background: theme.colors.primary.lighter
              },
              color: theme.palette.primary.main
            }}
            color="inherit"
            size="small"
          >
            <EditTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Conditionally render Delete button if deleteEndpoint is provided */}
      {props.deleteEndpoint && (
        <Tooltip title="Delete" arrow>
          <IconButton
            onClick={(e) =>
              actions.setModal({
                modalOpen: true,
                payload: props.payload,
                callback: props.callback,
                deleteEndpoint: props.deleteEndpoint,
                isDelete: true, // Set isDelete to true
                isEdit: false, // Ensure isEdit is false
                sourceAction: null
              })
            }
            sx={{
              '&:hover': { background: theme.colors.error.lighter },
              color: theme.palette.error.main
            }}
            color="inherit"
            size="small"
          >
            <DeleteTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {props.children}


    </TableCell>
  );
};

export interface TableSearchProps {
  searchData: Array<any>;
  setSearchData: (data: any) => void;
  placeholder?: string;
}

export const TableSearch = (props: TableSearchProps) => {
  const { setSearchData, searchData } = props;

  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem('searchTerm') || ''
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    localStorage.setItem('searchTerm', value);

    debounce(() => {
      const _search = (row: any) => {
        if (!row) return false;
        if (typeof row !== 'object') {
          return row.toString().toLowerCase().includes(value.toLowerCase());
        } else {
          return Object.values(row).filter(_search).length > 0;
        }
      };

      const result = Object.values(searchData).filter(_search);
      setSearchData(result);
    }, 1000)();
  };

  useEffect(() => {
    if (searchTerm) {
      handleSearch(searchTerm);
    }
  }, []);

  return (
    <Box display={'flex'} width={'44%'} margin={'0 auto'}>
      <TextField
        value={searchTerm}
        onChange={(e) => {
          handleSearch(e.target.value);
        }}
        id="outlined-start-adornment"
        sx={{ m: 1, width: '100%', padding: '0 2%' }}
        placeholder={props.placeholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ padding: '0 2%' }}>
              <SearchRounded />
            </InputAdornment>
          )
        }}
      />
    </Box>
  );
};

export const DeleteDialog = () => {
  const state = useUpdateModalStore((state) => state);
  const { enhancedFetch } = useAuth();

  const onAccept = async () => {
    await enhancedFetch(state.settings.deleteEndpoint, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: null
    });
    // call the callback function
    state.settings.callback();
    state.actions.setModal({ modalOpen: false });
  };
  return (
    <React.Fragment>
      <Dialog
        open={state.settings.modalOpen && state.settings.isDelete} // Only render if isDelete is true
        onClose={(e) =>
          state.actions.setModal({
            modalOpen: false,
            isEdit: false,
            isDelete: false
          })
        }
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <Delete></Delete>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Do you want to delete this record ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={(e) => state.actions.setModal({ modalOpen: false })}>
            Cancel
          </Button>
          <Button
            onClick={async (e) => {
              await onAccept();
            }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};
